import { ChevronRight, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Card, cx } from '../../components/ui';

/** A titled settings group: icon + heading above a card. */
export function SettingsSection({ icon: Icon, title, children, id }: { icon: LucideIcon; title: string; children: ReactNode; id?: string }) {
  return (
    <section className="flex flex-col gap-2.5" aria-labelledby={id}>
      <h2 id={id} className="flex items-center gap-2 px-1 text-[15px] font-bold tracking-wide text-[var(--muted)] uppercase">
        <Icon className="size-5" aria-hidden />
        {title}
      </h2>
      <Card className="overflow-hidden">{children}</Card>
    </section>
  );
}

/** Tappable navigation row inside a settings card. */
export function SettingsLink({ to, icon: Icon, title, desc }: { to: string; icon: LucideIcon; title: string; desc?: ReactNode }) {
  return (
    <Link to={to} className="flex min-h-16 items-center gap-4 px-5 py-3 transition hover:bg-[var(--surface-2)] active:bg-[var(--accent-soft)]">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-soft)] text-[var(--accent)]">
        <Icon className="size-5" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{title}</div>
        {desc ? <div className="text-[15px] text-[var(--muted)]">{desc}</div> : null}
      </div>
      <ChevronRight className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
    </Link>
  );
}

/** Large radio-style option (language, drawing mode). */
export function OptionCard({
  selected,
  onSelect,
  title,
  desc,
  icon: Icon,
  disabled,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  desc?: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cx(
        'flex min-h-14 w-full items-start gap-3 rounded-2xl border-2 px-4 py-3 text-left transition disabled:opacity-60',
        selected ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--line)] bg-[var(--surface)] hover:bg-[var(--surface-2)]',
      )}
    >
      <span
        aria-hidden
        className={cx(
          'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border-2',
          selected ? 'border-[var(--accent)]' : 'border-[var(--line)]',
        )}
      >
        {selected ? <span className="size-3 rounded-full bg-[var(--accent)]" /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 font-semibold">
          {Icon ? <Icon className="size-5 text-[var(--accent)]" aria-hidden /> : null}
          {title}
        </span>
        {desc ? <span className="mt-0.5 block text-[15px] text-[var(--muted)]">{desc}</span> : null}
      </span>
    </button>
  );
}
